/**
 * Weekly + biweekly marketing operations checklist.
 *
 * Each item has a cadence ('weekly' = every ISO week | 'biweekly' = every
 * 2 weeks, anchored to even ISO weeks). Completions are tracked per
 * (template_id, period_start) in marketing.checklist_run — so when a new
 * period begins, the item flips back to pending automatically.
 *
 * Edit this file to add/remove items; no DB migration needed.
 */

export type Cadence = 'weekly' | 'biweekly';

export type CheckCategory =
  | 'reviews'
  | 'competitive'
  | 'crm'
  | 'influencers'
  | 'aggregators'
  | 'ads'
  | 'content'
  | 'ops'
  | 'ratings';

export interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  cadence: Cadence;
  category: CheckCategory;
  /** Suggested owner role. Free-form display string. */
  owner: 'founder' | 'marketing' | 'ops' | 'sales';
  /** Optional in-app deep-link the user can jump to from the item. */
  to?: string;
  /** Optional external link (vendor portal, Google Business, etc). */
  external?: string;
}

export const CHECKLIST: ChecklistItem[] = [
  // ── WEEKLY ─────────────────────────────────────────────────────────────
  {
    id: 'growth-journey-review',
    title: 'Review Growth Journey (MoM + YoY)',
    description:
      'Open /growth — eyeball brand-level MoM and same-store YoY. Flag any outlet that flipped from green to red this week and add to the watch list.',
    cadence: 'weekly',
    category: 'ops',
    owner: 'founder',
    to: '/growth',
  },
  {
    id: 'reply-google-reviews',
    title: 'Reply to every new Google review',
    description:
      'Open Google Business → Reviews → reply to all unreplied from the last 7 days. Personal, specific, mention the dish if possible. Negative ones first, within 24h.',
    cadence: 'weekly',
    category: 'reviews',
    owner: 'marketing',
    to: '/reviews',
    external: 'https://business.google.com/reviews',
  },
  {
    id: 'reply-swiggy-zomato-reviews',
    title: 'Reply to every Swiggy + Zomato review (< 4★ first)',
    description:
      'Both vendor portals. Negative reviews triaged: identify dish + outlet + action taken. Sub-4-star reviews replied same-day going forward.',
    cadence: 'weekly',
    category: 'reviews',
    owner: 'marketing',
  },
  {
    id: 'active-fires',
    title: 'Review Active Fires per outlet',
    description:
      'Any outlet > -15% vs same-day-last-week? Same-day-last-month? Note root cause + immediate fix in the per-outlet ops log.',
    cadence: 'weekly',
    category: 'ops',
    owner: 'founder',
    to: '/today',
  },
  {
    id: 'competitor-teardown',
    title: 'Read this week\'s competitor "Why It Worked"',
    description:
      'Generate the weekly AI teardown on /competitive. Pin 1-2 replica briefs to next week\'s content plan.',
    cadence: 'weekly',
    category: 'competitive',
    owner: 'marketing',
    to: '/competitive',
  },
  {
    id: 'influencer-pipeline-triage',
    title: 'Influencer pipeline triage',
    description:
      'Anyone in "outreached" > 7 days with no reply? Move to "lost" or follow up. Anyone in "negotiating" > 14 days? Decision time.',
    cadence: 'weekly',
    category: 'influencers',
    owner: 'marketing',
    to: '/influencers',
  },
  {
    id: 'crm-lead-aging',
    title: 'CRM lead aging check',
    description:
      'Zoho: leads untouched > 7 days, deals stuck in same stage > 14 days. Assign owner or close.',
    cadence: 'weekly',
    category: 'crm',
    owner: 'sales',
    external: 'https://crm.zoho.in/crm/',
  },
  {
    id: 'ad-roas-pulse',
    title: 'Ad ROAS pulse — pause/scale decisions',
    description:
      'Meta + Google + Swiggy/Zomato ads. Anything < 2x ROAS sustained over 7 days = pause. Anything > 4x with low frequency = scale +50% budget.',
    cadence: 'weekly',
    category: 'ads',
    owner: 'marketing',
  },
  {
    id: 'sales-pace-vs-target',
    title: 'Sales pace vs month / quarter / year target',
    description:
      'Open /sales (Glance tab). Confirm month projection still hits target — if below 90%, name the gap, identify which 2-3 outlets are dragging, and what intervention to run this week (offers, ads, ops fix). Same logic for the quarter and year tiles.',
    cadence: 'weekly',
    category: 'ops',
    owner: 'founder',
    to: '/sales',
  },
  {
    id: 'petpooja-sync-health',
    title: 'Petpooja sync still running',
    description:
      'Open /sales → SyncBar at the top. Last-synced age should be < 6h. If older, hit Sync now (triggers the GitHub Action). If the workflow is failing, check gurmeetflax/flaxinventory2 → Actions.',
    cadence: 'weekly',
    category: 'ops',
    owner: 'ops',
    to: '/sales',
  },
  {
    id: 'availability-triage',
    title: 'Triage outlets < 95% online availability',
    description:
      'Open /aggregators → Availability per outlet. Any outlet at < 95% online (Swiggy) means it was offline during paid hours. Find the offline windows in vendor portal, identify cause (rider shortage, kitchen pause, internet, app glitch), fix + assign owner.',
    cadence: 'weekly',
    category: 'aggregators',
    owner: 'ops',
    to: '/aggregators',
  },
  {
    id: 'rating-drop-triage',
    title: 'Triage rating drops (Google + Swiggy + Zomato)',
    description:
      'Open /reviews. Any outlet flagged "severe" on the Rating Drops card? Walk through the bad reviews for that outlet, identify the root cause (item / packaging / rider / wait time / ops), set the fix + owner. Reply to every < 4★ review before closing the task.',
    cadence: 'weekly',
    category: 'ratings',
    owner: 'founder',
    to: '/reviews',
  },
  {
    id: 'content-shipped-count',
    title: 'Content shipped count vs plan',
    description:
      'How many IG posts + reels + LinkedIn posts went out this week? Plan target met? Capture the gap, queue make-up content for next week.',
    cadence: 'weekly',
    category: 'content',
    owner: 'marketing',
    to: '/meta',
  },
  {
    id: 'target-journey-flow',
    title: 'Open Target Journey — what is dragging the month?',
    description:
      'Open /journey. Walk through the lit circuit: any red lamps? Any blocked routes with rupee impact? Pick the single biggest drag (discount, availability, AOV, reach) and write a 2-line action plan for the week.',
    cadence: 'weekly',
    category: 'ops',
    owner: 'founder',
    to: '/journey',
  },
  {
    id: 'meta-roas-platform-split',
    title: 'Meta ROAS by platform split (IG reels vs FB feed)',
    description:
      'Open /meta → Ads. Which placement is doing the work this week — IG reels, IG feed, IG stories, or FB? True ROAS = Meta spend joined with Petpooja sales. Anything > 4x with low frequency = scale +50%. Anything < 2x sustained 7 days = pause.',
    cadence: 'weekly',
    category: 'ads',
    owner: 'marketing',
    to: '/meta',
  },
  {
    id: 'competitor-ig-teardown',
    title: 'Competitor IG teardown — pin ideas for next week',
    description:
      "Open /meta → Competitors. Walk through last week's top 3 posts per tracked competitor. Note the formats, hooks, hashtags that broke out. Pin 1-2 replica briefs to next week's content plan.",
    cadence: 'weekly',
    category: 'competitive',
    owner: 'marketing',
    to: '/meta',
  },
  {
    id: 'ad-library-sweep',
    title: 'Meta Ad Library competitor sweep',
    description:
      "Open /competitive → Ad Library. Review the past week's net-new ads from tracked competitors. Note formats, offers, audiences. Pin 1-2 angles worth A/B testing on Flax.",
    cadence: 'biweekly',
    category: 'competitive',
    owner: 'marketing',
    to: '/competitive',
  },
  {
    id: 'aggregator-discount-review',
    title: 'Aggregator discount review — pull what is not pulling weight',
    description:
      'Open /aggregators → Discounts. Confirm restaurant-funded share ≤ 15% of GMV across the chain. Any outlet > 20%, decide: keep the volume trade-off or pull the offer. Note which platform is heavier — negotiate matched funding from the lighter one.',
    cadence: 'weekly',
    category: 'aggregators',
    owner: 'marketing',
    to: '/aggregators',
  },

  // ── BIWEEKLY (every 15 days) ───────────────────────────────────────────
  {
    id: 'refresh-aggregator-offers',
    title: 'Refresh Swiggy + Zomato offers',
    description:
      'Both platforms downrank stale offers. Refresh anything > 14 days old. Avoid 30%+ blanket; layer 10-15% + free-item or bundle for AOV.',
    cadence: 'biweekly',
    category: 'aggregators',
    owner: 'marketing',
  },
  {
    id: 'crm-pipeline-review',
    title: 'CRM pipeline review meeting',
    description:
      'Whole sales + founder: walk through every active deal, identify next-action, set deadlines. Identify dead pipeline + recycle to nurture.',
    cadence: 'biweekly',
    category: 'crm',
    owner: 'founder',
    external: 'https://crm.zoho.in/crm/',
  },
  {
    id: 'corporate-outreach-batch',
    title: 'Corporate / bulk-order outreach batch',
    description:
      'Pull next 20 prospects from /corporate (or manual list). 3-touch sequence kicks off: email Day 0, WhatsApp Day 3, LinkedIn Day 7.',
    cadence: 'biweekly',
    category: 'crm',
    owner: 'sales',
  },
  {
    id: 'influencer-outreach-batch',
    title: 'Influencer outreach batch',
    description:
      'Top 10 highest fit-score creators from /influencers in "shortlisted". Draft + send. Move to "outreached".',
    cadence: 'biweekly',
    category: 'influencers',
    owner: 'marketing',
    to: '/influencers',
  },
  {
    id: 'aggregator-listing-audit',
    title: 'Aggregator listing spot-check (3 outlets)',
    description:
      'Rotate through outlets — pick 3 each fortnight. Open Swiggy + Zomato listing as a customer: hero photos current? Item photos? Badges? Hours right?',
    cadence: 'biweekly',
    category: 'aggregators',
    owner: 'ops',
  },
  {
    id: 'rating-trend-per-outlet',
    title: 'Rating trend per outlet (Google + Swiggy + Zomato)',
    description:
      'Flag any outlet dropping > 0.1★ in the fortnight. Root cause it (item quality, packaging, delivery rider, ops). Action plan, not just observation.',
    cadence: 'biweekly',
    category: 'ratings',
    owner: 'founder',
  },
  {
    id: 'top-spend-roas',
    title: 'Top-spend ROAS deep dive',
    description:
      'Top 3 highest-spend campaigns this fortnight. Creative still fresh? Audience still warm? Refresh creative if CTR halved.',
    cadence: 'biweekly',
    category: 'ads',
    owner: 'marketing',
  },
  {
    id: 'founder-content-batch',
    title: 'Founder content batch shoot',
    description:
      '2-3 LinkedIn posts + 1 reel hook. Topics from the marketing plan + warmed-prospects pipeline. Block 45 min in the calendar.',
    cadence: 'biweekly',
    category: 'content',
    owner: 'founder',
  },
];

export const CADENCE_LABELS: Record<Cadence, string> = {
  weekly: 'Every week',
  biweekly: 'Every 15 days',
};

export const CATEGORY_LABELS: Record<CheckCategory, string> = {
  reviews: 'Reviews',
  competitive: 'Competitive',
  crm: 'CRM / Sales',
  influencers: 'Influencers',
  aggregators: 'Aggregators',
  ads: 'Ads',
  content: 'Content',
  ops: 'Operations',
  ratings: 'Ratings',
};

export const OWNER_LABELS: Record<ChecklistItem['owner'], string> = {
  founder: 'Founder',
  marketing: 'Marketing',
  ops: 'Ops',
  sales: 'Sales',
};

/**
 * Period anchor: Monday of the current ISO week (UTC). For biweekly items
 * we snap further to even ISO weeks so an item only fires every 14 days
 * regardless of when the user opens the page.
 */
export function currentPeriodStart(cadence: Cadence, now = new Date()): string {
  const monday = mondayOf(now);
  if (cadence === 'weekly') return iso(monday);
  // Biweekly: snap back if the current ISO week is odd.
  const week = isoWeekNumber(monday);
  if (week % 2 === 1) monday.setUTCDate(monday.getUTCDate() - 7);
  return iso(monday);
}

function mondayOf(d: Date): Date {
  const utc = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dow = (utc.getUTCDay() + 6) % 7; // Mon = 0 ... Sun = 6
  utc.setUTCDate(utc.getUTCDate() - dow);
  return utc;
}

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function isoWeekNumber(d: Date): number {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}
